import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import toast from 'react-hot-toast';
import './BakimPlanlayici.css';
import html2pdf from 'html2pdf.js';

const SABIT_BAKIM_TIPLERI = [
  '2000 Saat Bakımı',
  '3000 Saat Bakımı',
  '4000 Saat Bakımı',
  '6000 Saat Bakımı'
];

const escapeHtml = (value = '') =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const benzersizId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const tarihFormatla = (tarih) => {
  if (!tarih) return '';
  const d = new Date(`${tarih}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
};

const sayiyaCevir = (deger, varsayilan = 0) => {
  const sayi = Number(deger);
  return Number.isFinite(sayi) ? sayi : varsayilan;
};

const apiIsteği = async (url, options = {}) => {
  const response = await fetch(url, options);

  if (!response.ok) {
    let hataMesaji = `İstek başarısız oldu: ${response.status}`;

    try {
      const data = await response.json();
      if (data?.hata) hataMesaji = data.hata;
      if (data?.message) hataMesaji = data.message;
      if (data?.error) hataMesaji = data.error;
    } catch {
      // Backend JSON döndürmeyebilir; varsayılan mesaj kullanılır.
    }

    throw new Error(hataMesaji);
  }

  return response;
};


const pdfDeger = (value) =>
  value === null || value === undefined || value === '' ? '—' : String(value);

const pdfTarih = (value) => {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  return `${match[3]}.${match[2]}.${match[1]}`;
};

const pdfMetinListesi = (value) =>
  (Array.isArray(value) ? value : [])
    .map(item => typeof item === 'object' && item !== null ? item.ad : item)
    .filter(item => item !== undefined && item !== null && String(item).trim() !== '')
    .map(String);

const pdfListe = (value) => {
  const liste = pdfMetinListesi(value);
  return liste.length
    ? `<ul>${liste.map(item => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`
    : '<p>Belirtilmedi.</p>';
};

const pdfBaslik = (baslik) => `
  <header class="pdf-header">
    <h1>MOON X24 AİR MAKİNA</h1>
    <h2>${escapeHtml(baslik)}</h2>
  </header>
`;

const pdfCihazBilgileri = (cihaz) => `
  <section class="pdf-section">
    <h3>Cihaz Bilgileri</h3>
    <p><strong>Firma:</strong> ${escapeHtml(pdfDeger(cihaz.firma))}</p>
    <p><strong>Model:</strong> ${escapeHtml(pdfDeger(cihaz.model))}</p>
    <p><strong>Seri no:</strong> ${escapeHtml(pdfDeger(cihaz.seriNo))}</p>
  </section>
`;

const pdfSaatler = (bakim) => `
  <div class="pdf-hours">
    <p><strong>Toplam:</strong> ${escapeHtml(pdfDeger(bakim.toplamCalismaSaati))} saat</p>
    <p><strong>Yükte:</strong> ${escapeHtml(pdfDeger(bakim.yukteCalismaSaati))} saat</p>
    <p><strong>Boşta:</strong> ${escapeHtml(pdfDeger(bakim.bostaCalismaSaati))} saat</p>
  </div>
`;

const pdfBakimKart = (bakim, index) => {
  const islemler = pdfMetinListesi(bakim.yapilanIslemler);
  const tipler = pdfMetinListesi(bakim.tipler);
  return `
    <section class="pdf-card">
      <h3>${index === undefined ? 'Bakım Detayları' : `Bakım #${index + 1}`} — ${escapeHtml(pdfTarih(bakim.tarih))}</h3>
      <p><strong>Durum:</strong> ${escapeHtml(pdfDeger(bakim.durum))}</p>
      <p><strong>Bakım süresi:</strong> ${escapeHtml(pdfDeger(bakim.bakimSaati))} saat</p>
      ${tipler.length ? `<p><strong>Bakım tipleri:</strong> ${escapeHtml(tipler.join(', '))}</p>` : ''}
      ${bakim.aciklama ? `<p><strong>Açıklama:</strong> ${escapeHtml(bakim.aciklama)}</p>` : ''}
      <h3>Çalışma Saatleri</h3>
      ${pdfSaatler(bakim)}
      <h3>Yapılan İşlemler</h3>
      ${pdfListe(islemler.length ? islemler : tipler)}
      <h3>Değiştirilen Parçalar</h3>
      ${pdfListe(bakim.parcaDegisimleri)}
      ${bakim.notlar ? `<h3>Teknik Notlar</h3><div class="pdf-note">${escapeHtml(bakim.notlar)}</div>` : ''}
    </section>
  `;
};

const pdfAltBilgi = () => `
  <footer class="pdf-footer">
    MOON X24 AİR MAKİNA — Bakım Kayıt Raporu<br>
    Oluşturulma: ${escapeHtml(new Date().toLocaleString('tr-TR'))}
  </footer>
`;

const BakimPlanlayici = () => {
  const [kompresorler, setKompresorler] = useState([]);
  const [bakimlar, setBakimlar] = useState([]);

  const [seciliKompresor, setSeciliKompresor] = useState(null);
  const [yapilanTarih, setYapilanTarih] = useState('');
  const [sonrakiTarih, setSonrakiTarih] = useState('');
  const [loading, setLoading] = useState(true);

  const [acikBolum, setAcikBolum] = useState(null);
  const [seciliBakimTipleri, setSeciliBakimTipleri] = useState([]);
  const [yeniTip, setYeniTip] = useState('');

  const [aramaTamamlanan, setAramaTamamlanan] = useState('');
  const [aramaPlanlanan, setAramaPlanlanan] = useState('');

  const [bakimNotlari, setBakimNotlari] = useState('');
  const [bakimSaati, setBakimSaati] = useState('');
  const [yapilanIslemler, setYapilanIslemler] = useState([]);
  const [parcaDegisimleri, setParcaDegisimleri] = useState([]);
  const [yeniParca, setYeniParca] = useState('');
  const [yeniIslem, setYeniIslem] = useState('');

  const [bakimSablonlari] = useState([]);
  const [seciliSablon, setSeciliSablon] = useState('');

  const [secilenFirma, setSecilenFirma] = useState('');
  const [secilenModel, setSecilenModel] = useState('');
  const [aramaTermi, setAramaTermi] = useState('');
  const [acilirMenu, setAcilirMenu] = useState(false);
  const [dropdownAcik, setDropdownAcik] = useState(false);

  const [tumTipler, setTumTipler] = useState(SABIT_BAKIM_TIPLERI);

  const [pdfOlusturuluyor, setPdfOlusturuluyor] = useState(false);

  const dropdownRef = useRef();
  const pdfIslemRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [kompRes, bakimRes] = await Promise.all([
          apiIsteği('/api/kayitlar'),
          apiIsteği('/api/bakimlar')
        ]);

        const kompresorData = await kompRes.json();
        const bakimData = await bakimRes.json();

        setKompresorler(Array.isArray(kompresorData) ? kompresorData : []);
        setBakimlar(Array.isArray(bakimData) ? bakimData : []);
      } catch (err) {
        console.error('Veri alınamadı:', err);
        toast.error(`❌ Veriler alınamadı: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownAcik(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const kayitli = JSON.parse(
      localStorage.getItem('ekBakimTipleri') || '[]'
    );

    if (Array.isArray(kayitli)) {
      setTumTipler([...SABIT_BAKIM_TIPLERI, ...kayitli]);
    }
  }, []);

  useEffect(() => {
    if (!seciliKompresor || !yapilanTarih) {
      setSonrakiTarih('');
      return;
    }

    const donguSaat = sayiyaCevir(seciliKompresor.bakimDongusuA, 0);
    const gunlukCalisma = sayiyaCevir(seciliKompresor.gunlukCalismaSaati, 8);

    if (donguSaat <= 0 || gunlukCalisma <= 0) {
      setSonrakiTarih('');
      return;
    }

    const baslangic = new Date(`${yapilanTarih}T00:00:00`);

    if (Number.isNaN(baslangic.getTime())) {
      setSonrakiTarih('');
      return;
    }

    const kalanGun = Math.ceil(donguSaat / gunlukCalisma);
    baslangic.setDate(baslangic.getDate() + kalanGun);

    setSonrakiTarih(tarihFormatla(
      `${baslangic.getFullYear()}-${String(baslangic.getMonth() + 1).padStart(2, '0')}-${String(baslangic.getDate()).padStart(2, '0')}`
    ));
  }, [seciliKompresor, yapilanTarih]);

  const firmalar = useMemo(
    () =>
      [...new Set(kompresorler.map((k) => k.firma).filter(Boolean))],
    [kompresorler]
  );

  const modeller = useMemo(
    () =>
      secilenFirma
        ? [
            ...new Set(
              kompresorler
                .filter((k) => k.firma === secilenFirma)
                .map((k) => k.model)
                .filter(Boolean)
            )
          ]
        : [],
    [kompresorler, secilenFirma]
  );

  const uygunKompresorler = useMemo(
    () =>
      kompresorler.filter(
        (k) => k.firma === secilenFirma && k.model === secilenModel
      ),
    [kompresorler, secilenFirma, secilenModel]
  );

  const handleKompresorSec = useCallback((komp) => {
    setSeciliKompresor({ ...komp });
    setSecilenFirma(komp.firma || '');
    setSecilenModel(komp.model || '');
    setAramaTermi(komp.firma || '');
    setAcilirMenu(false);
  }, []);

  const handleSaatDegisikligi = useCallback((e) => {
    const { name, value } = e.target;

    setSeciliKompresor((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleParcaEkle = useCallback(() => {
    const temizParca = yeniParca.trim();

    if (!temizParca) return;

    setParcaDegisimleri((prev) => [
      ...prev,
      {
        id: benzersizId(),
        ad: temizParca
      }
    ]);

    setYeniParca('');
  }, [yeniParca]);

  const handleParcaSil = useCallback((id) => {
    setParcaDegisimleri((prev) => prev.filter((parca) => parca.id !== id));
  }, []);

  const handleIslemEkle = useCallback(() => {
    const temizIslem = yeniIslem.trim();

    if (!temizIslem) return;

    setYapilanIslemler((prev) => [
      ...prev,
      {
        id: benzersizId(),
        ad: temizIslem
      }
    ]);

    setYeniIslem('');
  }, [yeniIslem]);

  const handleIslemSil = useCallback((id) => {
    setYapilanIslemler((prev) => prev.filter((islem) => islem.id !== id));
  }, []);

  const handleBakimEkle = async () => {
    if (!seciliKompresor || !yapilanTarih) {
      toast.error('⚠️ Lütfen kompresör ve tarih seçin.');
      return;
    }

    if (!seciliKompresor.seriNo) {
      toast.error('⚠️ Seçili kompresörün seri numarası bulunamadı.');
      return;
    }

    const donguSaat = sayiyaCevir(seciliKompresor.bakimDongusuA, 0);

    const aciklama =
      seciliBakimTipleri.length > 0
        ? `${seciliKompresor.model} için ${seciliBakimTipleri.join(', ')} işlemleri yapıldı`
        : `${seciliKompresor.model} için bakım yapıldı`;

    const yapilanIslemAdlari =
      yapilanIslemler.length > 0
        ? yapilanIslemler.map((islem) => islem.ad)
        : seciliBakimTipleri;

    const tamamBakim = {
      id: benzersizId(),
      tarih: yapilanTarih,
      firma: seciliKompresor.firma,
      kompresorModel: seciliKompresor.model,
      seriNo: seciliKompresor.seriNo,
      aciklama,
      durum: 'Tamamlandı',
      aktif: true,
      bakimSaati: bakimSaati === '' ? 0 : sayiyaCevir(bakimSaati, 0),
      notlar: bakimNotlari,
      tipler: seciliBakimTipleri,
      yapilanIslemler: yapilanIslemAdlari,
      parcaDegisimleri: parcaDegisimleri.map((parca) => parca.ad),
      toplamCalismaSaati: sayiyaCevir(seciliKompresor.toplamSaat, 0),
      yukteCalismaSaati: sayiyaCevir(seciliKompresor.yukteSaat, 0),
      bostaCalismaSaati: sayiyaCevir(seciliKompresor.bostaSaat, 0)
    };

    let sonrakiBakim = null;

    if (donguSaat > 0 && sonrakiTarih) {
      sonrakiBakim = {
        id: benzersizId(),
        tarih: sonrakiTarih,
        firma: seciliKompresor.firma,
        kompresorModel: seciliKompresor.model,
        seriNo: seciliKompresor.seriNo,
        aciklama: `${donguSaat} saatlik döngüye göre planlandı`,
        durum: 'Planlı',
        aktif: true
      };
    }

    const guncellenmisKompresorler = kompresorler.map((kompresor) =>
      kompresor.seriNo === seciliKompresor.seriNo
        ? {
            ...kompresor,
            sonBakimTarihi: yapilanTarih,
            toplamSaat: sayiyaCevir(seciliKompresor.toplamSaat, 0),
            yukteSaat: sayiyaCevir(seciliKompresor.yukteSaat, 0),
            bostaSaat: sayiyaCevir(seciliKompresor.bostaSaat, 0)
          }
        : kompresor
    );

    try {
      await apiIsteği('/api/bakimlar-ekle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([
          tamamBakim,
          ...(sonrakiBakim ? [sonrakiBakim] : [])
        ])
      });

      await apiIsteği('/api/kaydet-tum', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(guncellenmisKompresorler)
      });

      setBakimlar((prev) => [
        tamamBakim,
        ...(sonrakiBakim ? [sonrakiBakim] : []),
        ...prev
      ]);

      setKompresorler(guncellenmisKompresorler);

      setYapilanTarih('');
      setSonrakiTarih('');
      setSeciliBakimTipleri([]);
      setSeciliKompresor(null);
      setSecilenFirma('');
      setSecilenModel('');
      setAramaTermi('');
      setDropdownAcik(false);
      setBakimNotlari('');
      setBakimSaati('');
      setYapilanIslemler([]);
      setParcaDegisimleri([]);

      toast.success('✅ Bakım, plan ve saatler başarıyla kaydedildi!');
    } catch (err) {
      console.error('Bakım kaydı hatası:', err);
      toast.error(`❌ Kayıt sırasında hata oluştu: ${err.message}`);
    }
  };

  const tamamlananlar = useMemo(
    () =>
      bakimlar
        .filter(
          (bakim) =>
            bakim.durum === 'Tamamlandı' && bakim.aktif !== false
        )
        .sort(
          (a, b) => new Date(b.tarih) - new Date(a.tarih)
        ),
    [bakimlar]
  );

  const planlananlar = useMemo(
    () =>
      bakimlar
        .filter(
          (bakim) =>
            bakim.durum === 'Planlı' && bakim.aktif !== false
        )
        .map((bakim) => {
          const bugun = new Date();
          bugun.setHours(0, 0, 0, 0);

          const tarih = new Date(`${bakim.tarih}T00:00:00`);
          const fark = Math.ceil(
            (tarih - bugun) / (1000 * 60 * 60 * 24)
          );

          return {
            ...bakim,
            kalanGun: fark
          };
        })
        .sort((a, b) => a.kalanGun - b.kalanGun),
    [bakimlar]
  );

  const filtrelenmisTamamlananlar = useMemo(
    () =>
      tamamlananlar.filter((bakim) => {
        if (!aramaTamamlanan.trim()) return true;

        const arama = aramaTamamlanan.toLowerCase();

        return (
          (bakim.firma || '').toLowerCase().includes(arama) ||
          (bakim.kompresorModel || '').toLowerCase().includes(arama) ||
          (bakim.seriNo || '').toLowerCase().includes(arama) ||
          (bakim.tipler || []).join(', ').toLowerCase().includes(arama) ||
          (bakim.yapilanIslemler || []).join(', ').toLowerCase().includes(arama)
        );
      }),
    [tamamlananlar, aramaTamamlanan]
  );

  const filtrelenmisPlanlananlar = useMemo(
    () =>
      planlananlar.filter((bakim) => {
        if (!aramaPlanlanan.trim()) return true;

        const arama = aramaPlanlanan.toLowerCase();

        return (
          (bakim.firma || '').toLowerCase().includes(arama) ||
          (bakim.kompresorModel || '').toLowerCase().includes(arama) ||
          (bakim.seriNo || '').toLowerCase().includes(arama) ||
          (bakim.aciklama || '').toLowerCase().includes(arama)
        );
      }),
    [planlananlar, aramaPlanlanan]
  );

  const gecmisBakimlar = useMemo(
    () =>
      seciliKompresor
        ? tamamlananlar.filter(
            (bakim) =>
              bakim.seriNo === seciliKompresor.seriNo &&
              bakim.aktif !== false
          )
        : [],
    [tamamlananlar, seciliKompresor]
  );

  const handleEkle = useCallback(() => {
    const temizTip = yeniTip.trim();

    if (!temizTip) return;

    if (tumTipler.includes(temizTip)) {
      toast.error('⚠️ Bu bakım tipi zaten mevcut.');
      return;
    }

    const yeniListe = [...tumTipler, temizTip];

    setTumTipler(yeniListe);

    const mevcut = JSON.parse(
      localStorage.getItem('ekBakimTipleri') || '[]'
    );

    localStorage.setItem(
      'ekBakimTipleri',
      JSON.stringify([...mevcut, temizTip])
    );

    setYeniTip('');
    toast.success('✅ Bakım tipi eklendi');
  }, [yeniTip, tumTipler]);

  const handleSil = useCallback((tip) => {
    if (SABIT_BAKIM_TIPLERI.includes(tip)) {
      toast.error('⚠️ Bu tür sabit bir türdür ve silinemez.');
      return;
    }

    const mevcut = JSON.parse(
      localStorage.getItem('ekBakimTipleri') || '[]'
    );

    const yeniListe = mevcut.filter((kayitliTip) => kayitliTip !== tip);

    localStorage.setItem('ekBakimTipleri', JSON.stringify(yeniListe));

    setTumTipler((prev) => prev.filter((mevcutTip) => mevcutTip !== tip));
    setSeciliBakimTipleri((prev) =>
      prev.filter((mevcutTip) => mevcutTip !== tip)
    );

    toast.success('✅ Bakım tipi silindi');
  }, []);

  const toggleTip = useCallback((tip) => {
    setSeciliBakimTipleri((prev) =>
      prev.includes(tip)
        ? prev.filter((mevcutTip) => mevcutTip !== tip)
        : [...prev, tip]
    );
  }, []);

  const kaldirTip = useCallback((tip) => {
    setSeciliBakimTipleri((prev) =>
      prev.filter((mevcutTip) => mevcutTip !== tip)
    );
  }, []);

  const handleArşivleBakim = useCallback(
    async (bakimId) => {
      const bakimKaydi = bakimlar.find(
        (bakim) => bakim.id === bakimId && bakim.aktif !== false
      );

      if (!bakimKaydi) {
        toast.error('⚠️ Bakım kaydı bulunamadı veya zaten arşivlenmiş.');
        return;
      }

      try {
        await apiIsteği(`/api/bakimlar/${bakimKaydi.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ aktif: false })
        });

        setBakimlar((prev) =>
          prev.map((bakim) =>
            bakim.id === bakimKaydi.id
              ? { ...bakim, aktif: false }
              : bakim
          )
        );

        toast.success('✅ Bakım kaydı arşivlendi.');
      } catch (err) {
        console.error('Arşivleme hatası:', err);
        toast.error(`❌ Arşivleme başarısız: ${err.message}`);
      }
    },
    [bakimlar]
  );

  const pdfIndir = useCallback(async (icerik, dosyaAdi) => {
    if (pdfIslemRef.current) return;

    pdfIslemRef.current = true;
    setPdfOlusturuluyor(true);

    const alan = document.createElement('div');
    const perde = document.createElement('div');
    const rapor = document.createElement('div');

    alan.style.cssText = 'position:fixed;left:0;top:0;width:718px;height:100vh;overflow:auto;background:#fff;z-index:2147483645;pointer-events:none;';
    perde.style.cssText = 'position:fixed;inset:0;background:rgba(255,255,255,.97);z-index:2147483646;display:flex;align-items:center;justify-content:center;color:#1e40af;font:600 18px Arial,sans-serif;';
    perde.textContent = 'PDF hazırlanıyor, lütfen bekleyin...';
    perde.setAttribute('role', 'status');

    rapor.className = 'moon-bakim-pdf';
    rapor.style.cssText = 'display:block;position:static;visibility:visible;opacity:1;transform:none;width:190mm;max-width:none;min-height:0;height:auto;margin:0;padding:8mm;box-sizing:border-box;background:#fff;color:#111827;font:14px/1.5 Arial,sans-serif;text-align:left;overflow:visible;';
    rapor.innerHTML = `
      <style>
        .moon-bakim-pdf, .moon-bakim-pdf * { box-sizing: border-box; }
        .moon-bakim-pdf h1 { margin:0 0 6px; color:#1e40af; font:700 23px/1.3 Arial,sans-serif; }
        .moon-bakim-pdf h2 { margin:0; color:#475569; font:600 18px/1.4 Arial,sans-serif; }
        .moon-bakim-pdf h3 { margin:0 0 10px; color:#1e40af; font:700 16px/1.4 Arial,sans-serif; }
        .moon-bakim-pdf p { margin:6px 0; overflow-wrap:anywhere; }
        .moon-bakim-pdf ul { margin:6px 0 12px; padding-left:22px; }
        .moon-bakim-pdf li { margin-bottom:4px; overflow-wrap:anywhere; }
        .moon-bakim-pdf .pdf-header { text-align:center; border-bottom:2px solid #1e40af; padding-bottom:16px; margin-bottom:20px; }
        .moon-bakim-pdf .pdf-section { margin-bottom:18px; }
        .moon-bakim-pdf .pdf-card { border:1px solid #d1d5db; border-radius:6px; padding:14px; margin-bottom:16px; }
        .moon-bakim-pdf .pdf-note { white-space:pre-wrap; overflow-wrap:anywhere; padding:12px; background:#f3f4f6; border-left:3px solid #1e40af; }
        .moon-bakim-pdf .pdf-hours { padding:10px; background:#f8fafc; border:1px solid #e5e7eb; }
        .moon-bakim-pdf .pdf-footer { margin-top:20px; padding-top:12px; border-top:1px solid #d1d5db; text-align:center; color:#64748b; font-size:11px; }
      </style>
      ${icerik}
    `;

    try {
      alan.appendChild(rapor);
      document.body.appendChild(alan);
      document.body.appendChild(perde);

      if (document.fonts?.ready) await document.fonts.ready;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      if (!rapor.textContent.trim() || rapor.getBoundingClientRect().width === 0) {
        throw new Error('Rapor içeriği oluşturulamadı.');
      }

      const yukseklik = Math.ceil(rapor.scrollHeight);
      const olcek = yukseklik > 7000 ? 1 : 2;
      if (yukseklik * olcek > 30000) {
        throw new Error('Rapor tarayıcının tek seferde görüntüleyebileceği boyutu aşıyor. Daha az bakım için detaylı rapor alın.');
      }

      const temizAd = String(dosyaAdi || 'bakim_raporu')
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
        .replace(/\.pdf$/i, '')
        .slice(0, 160);

      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `${temizAd}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: olcek,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: 1024,
            windowHeight: Math.max(900, yukseklik),
            onclone: (klonDokuman) => {
              klonDokuman.querySelectorAll('.moon-bakim-pdf').forEach((el) => {
                el.style.setProperty('display', 'block', 'important');
                el.style.setProperty('visibility', 'visible', 'important');
                el.style.setProperty('opacity', '1', 'important');
                el.style.setProperty('position', 'static', 'important');
                el.style.setProperty('transform', 'none', 'important');
                el.style.setProperty('overflow', 'visible', 'important');
              });
            }
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'], avoid: ['h1', 'h2', 'h3', 'li'] }
        })
        .from(rapor)
        .save();

      toast.success('PDF raporu oluşturuldu.');
    } catch (err) {
      console.error('PDF oluşturma hatası:', err);
      toast.error(`PDF oluşturulamadı: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      alan.remove();
      perde.remove();
      pdfIslemRef.current = false;
      setPdfOlusturuluyor(false);
    }
  }, []);

  const generatePDF = useCallback(async () => {
    if (!seciliKompresor) {
      toast.error('Lütfen bir kompresör seçin.');
      return;
    }

    const formIslemleri = yapilanIslemler.map(islem => islem.ad);
    const ozet = `
      ${pdfBaslik('Bakım Raporu')}
      ${pdfCihazBilgileri(seciliKompresor)}
      <section class="pdf-section">
        <h3>Çalışma Saatleri</h3>
        ${pdfSaatler({
          toplamCalismaSaati: seciliKompresor.toplamSaat,
          yukteCalismaSaati: seciliKompresor.yukteSaat,
          bostaCalismaSaati: seciliKompresor.bostaSaat
        })}
      </section>
      <section class="pdf-section">
        <h3>Formdaki Bakım Bilgileri (henüz kaydedilmemiş olabilir)</h3>
        <p><strong>Bakım tarihi:</strong> ${escapeHtml(pdfTarih(yapilanTarih))}</p>
        <p><strong>Sonraki bakım:</strong> ${escapeHtml(pdfTarih(sonrakiTarih))}</p>
        <p><strong>Bakım süresi:</strong> ${escapeHtml(bakimSaati === '' ? '—' : bakimSaati)} saat</p>
        <p><strong>Bakım tipleri:</strong> ${escapeHtml(seciliBakimTipleri.join(', ') || '—')}</p>
        <h3>Yapılan İşlemler</h3>
        ${pdfListe(formIslemleri.length ? formIslemleri : seciliBakimTipleri)}
        <h3>Değiştirilen Parçalar</h3>
        ${pdfListe(parcaDegisimleri)}
        ${bakimNotlari ? `<h3>Teknik Notlar</h3><div class="pdf-note">${escapeHtml(bakimNotlari)}</div>` : ''}
      </section>
      <section class="pdf-section">
        <h3>Kayıtlı Bakım Geçmişi (${gecmisBakimlar.length})</h3>
        ${gecmisBakimlar.length ? gecmisBakimlar.map((bakim, index) => pdfBakimKart(bakim, index)).join('') : '<p>Kayıtlı geçmiş bakım bulunamadı.</p>'}
      </section>
      ${pdfAltBilgi()}
    `;
    await pdfIndir(ozet, `${seciliKompresor.model}_${seciliKompresor.seriNo}_bakim_raporu`);
  }, [seciliKompresor, yapilanTarih, sonrakiTarih, bakimSaati, seciliBakimTipleri, yapilanIslemler, parcaDegisimleri, bakimNotlari, gecmisBakimlar, pdfIndir]);

  const generateDetayliRaporPDF = useCallback(async (bakim) => {
    if (!bakim) return;
    const icerik = `
      ${pdfBaslik('Detaylı Bakım Raporu')}
      ${pdfCihazBilgileri({ firma: bakim.firma, model: bakim.kompresorModel, seriNo: bakim.seriNo })}
      ${pdfBakimKart(bakim)}
      ${pdfAltBilgi()}
    `;
    await pdfIndir(icerik, `${bakim.firma}_${bakim.kompresorModel}_${bakim.tarih}_detayli_rapor`);
  }, [pdfIndir]);

  const generateTopluGecmisRaporPDF = useCallback(async () => {
    if (!seciliKompresor || !gecmisBakimlar.length) {
      toast.error('Geçmiş bakım kaydı bulunamadı.');
      return;
    }
    const icerik = `
      ${pdfBaslik('Geçmiş Bakım Raporu')}
      ${pdfCihazBilgileri(seciliKompresor)}
      <p><strong>Toplam bakım:</strong> ${gecmisBakimlar.length}</p>
      ${gecmisBakimlar.map((bakim, index) => pdfBakimKart(bakim, index)).join('')}
      ${pdfAltBilgi()}
    `;
    await pdfIndir(icerik, `${seciliKompresor.firma}_${seciliKompresor.model}_gecmis_rapor`);
  }, [seciliKompresor, gecmisBakimlar, pdfIndir]);

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  return (
    <div className="bakim-planlayici">
      <header className="header">
        <h1>🔧 Bakım Planlayıcı</h1>
      </header>

      <section className="form-section">
        <div className="form-group">
          <label>Firma</label>

          <div className="firma-search-wrapper">
            <input
              type="text"
              placeholder="Firma adı yazarak ara..."
              value={aramaTermi}
              onChange={(e) => {
                setAramaTermi(e.target.value);
                setSecilenModel('');
                setSeciliKompresor(null);
                setAcilirMenu(true);
              }}
              onFocus={() => {
                if (firmalar.length > 0) {
                  setAcilirMenu(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setAcilirMenu(false), 150);
              }}
              className="firma-search-input"
            />

            {acilirMenu && aramaTermi && (
              <ul className="autocomplete-list">
                {firmalar
                  .filter((firma) =>
                    firma.toLowerCase().includes(aramaTermi.toLowerCase())
                  )
                  .slice(0, 10)
                  .map((firma) => (
                    <li
                      key={firma}
                      onClick={() => {
                        setSecilenFirma(firma);
                        setAramaTermi(firma);
                        setAcilirMenu(false);
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {firma}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>

        {secilenFirma && !acilirMenu && (
          <div className="form-group">
            <label>Model</label>

            <select
              value={secilenModel}
              onChange={(e) => {
                setSecilenModel(e.target.value);
                setSeciliKompresor(null);
              }}
            >
              <option value="">Model Seçiniz</option>

              {modeller.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>
        )}

        {secilenModel && !acilirMenu && (
          <div className="form-group">
            <label>Kompresör (Seri No)</label>

            <div className="kompresor-list">
              {uygunKompresorler.map((kompresor) => (
                <div
                  key={kompresor.seriNo || benzersizId()}
                  className={`kompresor-item ${
                    seciliKompresor?.seriNo === kompresor.seriNo
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() => handleKompresorSec(kompresor)}
                >
                  🔧 {kompresor.model} | 📛 {kompresor.seriNo}
                </div>
              ))}
            </div>
          </div>
        )}

        {seciliKompresor && (
          <div className="info-panel">
            <h3>✏️ Cihaz Bilgilerini Güncelle</h3>

            <div className="info-grid">
              <div>
                <label>Toplam Saat</label>
                <input
                  type="number"
                  name="toplamSaat"
                  value={seciliKompresor.toplamSaat || ''}
                  onChange={handleSaatDegisikligi}
                  className="input-small"
                />
              </div>

              <div>
                <label>Yükte Saat</label>
                <input
                  type="number"
                  name="yukteSaat"
                  value={seciliKompresor.yukteSaat || ''}
                  onChange={handleSaatDegisikligi}
                  className="input-small"
                />
              </div>

              <div>
                <label>Boşta Saat</label>
                <input
                  type="number"
                  name="bostaSaat"
                  value={seciliKompresor.bostaSaat || ''}
                  onChange={handleSaatDegisikligi}
                  className="input-small"
                />
              </div>

              <div>
                <label>Günlük Ortalama</label>
                <input
                  type="number"
                  name="gunlukCalismaSaati"
                  value={seciliKompresor.gunlukCalismaSaati || 8}
                  onChange={handleSaatDegisikligi}
                  className="input-small"
                />
              </div>

              <div>
                <label>Son Bakım Tarihi</label>
                <input
                  type="date"
                  name="sonBakimTarihi"
                  value={seciliKompresor.sonBakimTarihi || ''}
                  onChange={handleSaatDegisikligi}
                  className="input-small"
                />
              </div>

              <div>
                <label>Bakım Döngüsü (saat)</label>
                <input
                  type="number"
                  name="bakimDongusuA"
                  value={seciliKompresor.bakimDongusuA || ''}
                  onChange={handleSaatDegisikligi}
                  className="input-small"
                />
              </div>
            </div>
          </div>
        )}

        {seciliKompresor && (
          <>
            <div className="form-group">
              <label>Yapılan Bakım İşlemleri</label>

              <div className="selected-tags">
                {seciliBakimTipleri.map((tip) => (
                  <div key={tip} className="tag">
                    {tip}

                    <button
                      type="button"
                      onClick={() => kaldirTip(tip)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>

              <div className="dropdown-select" ref={dropdownRef}>
                <div
                  className="dropdown-header"
                  onClick={() => setDropdownAcik(!dropdownAcik)}
                >
                  Bakım işlemi seçin... {dropdownAcik ? '▲' : '▼'}
                </div>

                {dropdownAcik && (
                  <div className="dropdown-list">
                    {tumTipler.map((tip) => (
                      <div
                        key={tip}
                        className={`dropdown-item ${
                          seciliBakimTipleri.includes(tip) ? 'selected' : ''
                        }`}
                        onClick={() => toggleTip(tip)}
                      >
                        {tip}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!dropdownAcik && (
              <>
                <div className="form-group">
                  <label>📋 Detaylı İşlem Notları</label>

                  <div className="islem-ekleme-wrapper">
                    <input
                      type="text"
                      placeholder="Yapılan işlem ekleyin..."
                      value={yeniIslem}
                      onChange={(e) => setYeniIslem(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleIslemEkle();
                        }
                      }}
                    />

                    <button
                      type="button"
                      onClick={handleIslemEkle}
                      className="btn primary"
                      style={{ width: 'auto' }}
                    >
                      Ekle
                    </button>
                  </div>

                  {yapilanIslemler.length > 0 && (
                    <div className="selected-tags">
                      {yapilanIslemler.map((islem) => (
                        <div
                          key={islem.id}
                          className="tag islem-tag"
                        >
                          {islem.ad}

                          <button
                            type="button"
                            onClick={() => handleIslemSil(islem.id)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>🔧 Değiştirilen Parçalar</label>

                  <div className="islem-ekleme-wrapper">
                    <input
                      type="text"
                      placeholder="Değiştirilen parça ekleyin..."
                      value={yeniParca}
                      onChange={(e) => setYeniParca(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleParcaEkle();
                        }
                      }}
                    />

                    <button
                      type="button"
                      onClick={handleParcaEkle}
                      className="btn primary"
                      style={{ width: 'auto' }}
                    >
                      Ekle
                    </button>
                  </div>

                  {parcaDegisimleri.length > 0 && (
                    <div className="selected-tags">
                      {parcaDegisimleri.map((parca) => (
                        <div
                          key={parca.id}
                          className="tag parca-tag"
                        >
                          {parca.ad}

                          <button
                            type="button"
                            onClick={() => handleParcaSil(parca.id)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>⏱️ Bakım Süresi (Saat)</label>

                  <input
                    type="number"
                    placeholder="Örn: 2.5"
                    value={bakimSaati}
                    onChange={(e) => setBakimSaati(e.target.value)}
                    step="0.5"
                    min="0"
                    className="bakim-suresi-input"
                  />
                </div>

                <div className="form-group">
                  <label>📝 Teknik Notlar</label>

                  <textarea
                    placeholder="Bakım sırasında gözlemlenenler, öneriler, özel durumlar..."
                    value={bakimNotlari}
                    onChange={(e) => setBakimNotlari(e.target.value)}
                    rows="4"
                    className="teknik-notlar-textarea"
                  />
                </div>

                <div className="form-group">
                  <label>Bakım Yapıldı Tarihi</label>

                  <input
                    type="date"
                    value={yapilanTarih}
                    onChange={(e) => setYapilanTarih(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {sonrakiTarih && (
                  <div className="next-date-info">
                    📅 <strong>Sonraki bakım:</strong> {sonrakiTarih}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleBakimEkle}
                  className="btn primary"
                >
                  ✅ Kaydet ve Planla
                </button>

                <button
                  type="button"
                  onClick={generatePDF}
                  className="btn secondary"
                  disabled={pdfOlusturuluyor}
                >
                  {pdfOlusturuluyor
                    ? '⏳ PDF hazırlanıyor...'
                    : '📄 PDF Rapor Al'}
                </button>
              </>
            )}
          </>
        )}
      </section>

      {seciliKompresor && (
        <section className="list-section gecmis-rapor-section">
          <div className="gecmis-rapor-header">
            <h2 className="gecmis-rapor-title">
              📊 Geçmiş Bakım Raporları - {seciliKompresor.model} (
              {seciliKompresor.seriNo})
            </h2>

            {gecmisBakimlar.length > 0 && (
              <button
                type="button"
                onClick={generateTopluGecmisRaporPDF}
                className="btn-toplu-rapor"
                disabled={pdfOlusturuluyor}
              >
                {pdfOlusturuluyor
                  ? '⏳ PDF hazırlanıyor...'
                  : '📄 Tüm Geçmiş Raporu (PDF)'}
              </button>
            )}
          </div>

          {gecmisBakimlar.length > 0 ? (
            <div className="gecmis-rapor-grid">
              {gecmisBakimlar.map((bakim) => (
                <div
                  key={bakim.id || benzersizId()}
                  className="gecmis-bakim-kart"
                >
                  <div className="gecmis-bakim-kart-header">
                    <div>
                      <h3 className="gecmis-bakim-tarih">
                        {new Date(`${bakim.tarih}T00:00:00`).toLocaleDateString('tr-TR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </h3>

                      <p className="gecmis-bakim-sure">
                        Bakım Süresi:{' '}
                        <strong>{bakim.bakimSaati || '0'} saat</strong>
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => generateDetayliRaporPDF(bakim)}
                      className="btn-detayli-rapor"
                      disabled={pdfOlusturuluyor}
                    >
                      {pdfOlusturuluyor
                        ? '⏳ Hazırlanıyor...'
                        : '📄 Detaylı Rapor'}
                    </button>
                  </div>

                  {(bakim.yapilanIslemler || []).length > 0 && (
                    <div className="islem-bolgesi">
                      <strong className="islem-bolgesi-strong">
                        Yapılan İşlemler:
                      </strong>

                      <ul className="islem-listesi">
                        {bakim.yapilanIslemler.map((islem, index) => (
                          <li key={`${bakim.id}-islem-${index}`}>
                            {typeof islem === 'string' ? islem : islem?.ad}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(bakim.parcaDegisimleri || []).length > 0 && (
                    <div className="parca-bolgesi">
                      <strong className="parca-bolgesi-strong">
                        Değiştirilen Parçalar:
                      </strong>

                      <div className="parca-chip-wrapper">
                        {bakim.parcaDegisimleri.map((parca, index) => (
                          <span
                            key={`${bakim.id}-parca-${index}`}
                            className="parca-chip"
                          >
                            {typeof parca === 'string' ? parca : parca?.ad}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {bakim.notlar && (
                    <div className="not-bolgesi">
                      <strong className="not-bolgesi-strong">
                        Teknik Notlar:
                      </strong>

                      <p className="not-icerik">{bakim.notlar}</p>
                    </div>
                  )}

                  <div className="saat-bilgileri">
                    <span>
                      <strong>Toplam:</strong>{' '}
                      {bakim.toplamCalismaSaati || '—'} saat
                    </span>

                    <span>
                      <strong>Yükte:</strong>{' '}
                      {bakim.yukteCalismaSaati || '—'} saat
                    </span>

                    <span>
                      <strong>Boşta:</strong>{' '}
                      {bakim.bostaCalismaSaati || '—'} saat
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bos-uyari">
              ⚠️ Bu kompresör için henüz kayıtlı geçmiş bakım bulunmamaktadır.
            </div>
          )}
        </section>
      )}

      <section className="list-section">
        <div className="section-header">
          <h2
            className="collapsible-header"
            onClick={() =>
              setAcikBolum(
                acikBolum === 'tamamlanan' ? null : 'tamamlanan'
              )
            }
          >
            ✅ Tamamlanan Bakımlar{' '}
            {acikBolum === 'tamamlanan' ? '▲' : '▼'}
          </h2>

          {acikBolum === 'tamamlanan' && (
            <input
              type="text"
              placeholder="Ara: firma, model, işlem..."
              value={aramaTamamlanan}
              onChange={(e) => setAramaTamamlanan(e.target.value)}
              className="search-input"
            />
          )}
        </div>

        {acikBolum === 'tamamlanan' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Firma</th>
                <th>Model</th>
                <th>Seri No</th>
                <th>İşlemler</th>
                <th>Durum</th>
                <th>İşlem</th>
              </tr>
            </thead>

            <tbody>
              {filtrelenmisTamamlananlar.length > 0 ? (
                filtrelenmisTamamlananlar.map((bakim) => (
                  <tr key={bakim.id || benzersizId()}>
                    <td>{bakim.tarih}</td>
                    <td>{bakim.firma}</td>
                    <td>{bakim.kompresorModel}</td>
                    <td>{bakim.seriNo}</td>

                    <td>
                      {bakim.tipler?.length > 0
                        ? bakim.tipler.join(', ')
                        : bakim.yapilanIslemler?.length > 0
                        ? bakim.yapilanIslemler
                            .map((islem) =>
                              typeof islem === 'string'
                                ? islem
                                : islem?.ad
                            )
                            .join(', ')
                        : 'Genel'}
                    </td>

                    <td>
                      <span className="status completed">
                        {bakim.durum}
                      </span>
                    </td>

                    <td>
                      <button
                        type="button"
                        onClick={() => handleArşivleBakim(bakim.id)}
                        className="btn-sil"
                      >
                        Arşivle
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    Hiç kayıt bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      <section className="list-section">
        <div className="section-header">
          <h2
            className="collapsible-header"
            onClick={() =>
              setAcikBolum(
                acikBolum === 'planlanan' ? null : 'planlanan'
              )
            }
          >
            Planlanan Bakımlar{' '}
            {acikBolum === 'planlanan' ? '▲' : '▼'}
          </h2>

          {acikBolum === 'planlanan' && (
            <input
              type="text"
              placeholder="Ara: firma, model, açıklama..."
              value={aramaPlanlanan}
              onChange={(e) => setAramaPlanlanan(e.target.value)}
              className="search-input"
            />
          )}
        </div>

        {acikBolum === 'planlanan' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Firma</th>
                <th>Model</th>
                <th>Seri No</th>
                <th>Kalan Gün</th>
                <th>Durum</th>
                <th>İşlem</th>
              </tr>
            </thead>

            <tbody>
              {filtrelenmisPlanlananlar.length > 0 ? (
                filtrelenmisPlanlananlar.map((bakim) => {
                  const bugun = new Date();
                  bugun.setHours(0, 0, 0, 0);

                  const tarih = new Date(`${bakim.tarih}T00:00:00`);
                  const gecikmis = tarih < bugun;
                  const kalanGun = bakim.kalanGun;

                  const statusClass = gecikmis
                    ? 'delayed'
                    : kalanGun <= 7
                    ? 'warning'
                    : 'planned';

                  const kalanText = gecikmis
                    ? `${Math.abs(kalanGun)} gün geçti`
                    : `${kalanGun} gün kaldı`;

                  return (
                    <tr key={bakim.id || benzersizId()}>
                      <td>{bakim.tarih}</td>
                      <td>{bakim.firma}</td>
                      <td>{bakim.kompresorModel}</td>
                      <td>{bakim.seriNo}</td>

                      <td>
                        <strong>{kalanText}</strong>
                      </td>

                      <td>
                        <span className={`status ${statusClass}`}>
                          {gecikmis ? 'Geçmiş' : bakim.durum}
                        </span>
                      </td>

                      <td>
                        <button
                          type="button"
                          onClick={() => handleArşivleBakim(bakim.id)}
                          className="btn-sil"
                        >
                          Arşivle
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    Hiç kayıt bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

    </div>
  );
};

export default BakimPlanlayici;
